import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, from, interval, Subscription, switchMap } from 'rxjs';
import { API, NotificationDTO, Order, Pagination, shared_Notification_retrieveAll_order } from '../api/api';
import { ErrorService } from '../error.service';
import { PermissionService } from '../permission.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private chimesPath: string = '/assets/audio/chimes.mp3';
  private chimes: HTMLAudioElement;

  private sub: Subscription;
  private apisub: Subscription;

  private requestInProgress: boolean = false;

  private requestOptions: {
    search?: string;
    read?: boolean;
    archived?: boolean;
    status?: string[];
    category?: string[];
    id?: number[];
    order?: { by: shared_Notification_retrieveAll_order; param?: string; direction: Order };
  } = {
    read: false,
    archived: false,
  };

  public onNewNotifications: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private api: API,
    private errorHandler: ErrorService,
    public permissionService: PermissionService
  ) {
    this._pendingNotifications = [];
    this.chimes = new Audio();
    this.chimes.src = this.chimesPath;
    this.chimes.load();
  }

  private _pendingNotifications: NotificationWithRouterLinkDTO[];

  get pendingNotifications(): NotificationWithRouterLinkDTO[] {
    return this._pendingNotifications;
  }

  private _pendingNotificationsCount: number;

  get pendingNotificationsCount(): number {
    return this._pendingNotificationsCount;
  }

  private _totalPendingNotificationsCount: number;

  get totalPendingNotificationsCount(): number {
    return this._totalPendingNotificationsCount;
  }

  private _morePendingNotificationsCount: number;

  get morePendingNotificationsCount(): number {
    return this._morePendingNotificationsCount;
  }

  public restart() {
    this.stop();
    if (this.permissionService.hasPermission(this.permissionService.Permission.notification_list)) {
      this.request().then(() => {
        this.resub();
      });
    } else {
      setTimeout(() => {
        this.restart();
      }, 60 * 1000);
    }
  }

  stop() {
    this.unsubTimer();
    this.unsubApi();
    this.requestInProgress = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private resub() {
    this.sub = interval(5000)
      .pipe(switchMap(() => from(localStorage.getItem('last_known_authorization'))))
      .subscribe((result: string | null) => {
        if (result) {
          this.request();
        } else {
          this.stop();
        }
      });
  }

  private unsubTimer() {
    if (this.sub) {
      try {
        this.sub.unsubscribe();
        this.sub = null;
      } catch (err) {
        this.errorHandler.errorHandler(err);
      }
    }
  }

  private unsubApi() {
    if (this.apisub) {
      try {
        this.apisub.unsubscribe();
        this.apisub = null;
      } catch (err) {
        this.errorHandler.errorHandler(err);
      }
    }
  }

  private onRequestEnd() {
    this.unsubApi();
    this.requestInProgress = false;
  }

  private request(): Promise<void> {
    return new Promise(resolve => {
      if (!this.requestInProgress) {
        this.requestInProgress = true;
        const page = 1;
        const count = 12;
        const pagination = new BehaviorSubject<Pagination>({ page, count, pages: -1, resultcount: -1 });
        this.apisub = this.api.shared_Notification_retrieveAll(this.requestOptions, pagination).subscribe(
          (data: NotificationDTO[]) => {
            this.recalculateNotifications(data, pagination.getValue());
            resolve();
          },
          error => {
            if (error.status === 401) {
              this.stop();
            } else {
              this.errorHandler.errorHandler(error);
              this.onRequestEnd();
              resolve();
            }
          }
        );
      }
    });
  }

  private recalculateNotifications(data: NotificationDTO[], pagination: Pagination) {
    let newNotifications: boolean = false;
    let doChime: boolean;

    for (const notificationFetched of data) {
      let contains: boolean = false;
      for (const oldNotification of this._pendingNotifications) {
        // check if array would change
        if (notificationFetched.id === oldNotification.id) {
          contains = true;
          break;
        }
      }
      if (!contains) {
        newNotifications = true;
      }
    }
    this.recalculateCounters(pagination, data);

    if (data.length > 0) {
      // check if there is a NEWER notification up top
      doChime = this._pendingNotifications.length === 0 || data[0].id > this._pendingNotifications[0].id;
    } else {
      newNotifications = true;
      doChime = false;
    }

    if (newNotifications) {
      this._pendingNotifications = [];
      for (const notification of data) {
        switch (notification.category?.id) {
          case 'advertiser_planning_accepted':
            (notification as NotificationWithRouterLinkDTO).routerLink = '/content/campaigns/management';
            break;
          case 'advertiser_planning_submitted':
            (notification as NotificationWithRouterLinkDTO).routerLink =
              '/content/campaigns/plannings/planning/' + notification?.entity?.id;
            break;
          case 'advertiser_verification_rejected':
            (notification as NotificationWithRouterLinkDTO).routerLink = '/content/campaigns/management';
            break;
          case 'publisher_campaign_ending_tomorrow':
            break;
          case 'publisher_campaign_starting_tomorrow':
            break;
          case 'publisher_commercial_not_playing':
            break;
          case 'publisher_device_not_playing_enough':
          case 'publisher_device_offline_too_long':
          case 'publisher_turned_down_too_long':
            (notification as NotificationWithRouterLinkDTO).routerLink = '/network/devices/device-details/' + notification?.entity?.id;
            break;
          case 'publisher_verification_pending':
            (notification as NotificationWithRouterLinkDTO).routerLink = '/network/verification/';
            break;
          case 'system_hibernate_error':
            break;
          default:
            break;
        }
      }
      this._pendingNotifications = data;
    }

    if (doChime) {
      this.onNewNotifications.emit();
      this.chime();
    }

    this.onRequestEnd();
  }

  private recalculateCounters(pagination: Pagination, data: NotificationDTO[]) {
    const total = pagination.resultcount;
    const count = data.length;
    const more = total - count;

    this._totalPendingNotificationsCount = total;
    this._pendingNotificationsCount = count;
    this._morePendingNotificationsCount = more;
  }

  public chime() {
    // new Audio(this.chimesPath).play();
  }

  public markAsRead(notification: NotificationDTO): Promise<NotificationDTO> {
    return new Promise((resolve, rej) => {
      this.unsubTimer();
      this.unsubApi();
      this.api.shared_Notification_readById(notification.id).subscribe(
        (res: NotificationDTO) => {
          this.request().then(() => {
            this.resub();
            resolve(res);
          });
        },
        error => {
          this.request().then(() => {
            this.resub();
            rej(error);
          });
        }
      );
    });
  }

  public archive(notification: NotificationDTO): Promise<NotificationDTO> {
    return new Promise((resolve, rej) => {
      this.api.shared_Notification_archiveById(notification.id).subscribe(
        (res: NotificationDTO) => {
          this.request().then(() => {
            this.resub();
            resolve(res);
          });
        },
        error => {
          this.request().then(() => {
            this.resub();
            rej(error);
          });
        }
      );
    });
  }
}

export class NotificationWithRouterLinkDTO extends NotificationDTO {
  routerLink?: string;
}
